/** Resources on duty: who on shift (or on standby) is BA, SFST, drone, less lethal, EMT, paramedic, entry shield… */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { Badge, Card, ErrorBox, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { when } from './common.tsx';

const inHours = (h: number) => {
  const d = new Date(Date.now() + h * 3600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function MyStandby({ d, onDone }: { d: any; onDone: () => void }) {
  const mine = d.standby.find((s: any) => s.id === d.me);
  const [until, setUntil] = useState(inHours(12));
  const [note, setNote] = useState('');
  const a = useAction();
  return (
    <Card title="Me on standby">
      {mine ? (
        <div className="row gap-sm wrap">
          <span>
            You’re on standby until <strong>{when(mine.until)}</strong>
            {mine.note ? ` · ${mine.note}` : ''}
          </span>
          <button className="btn sm" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/resources/standby/${d.me}/clear`), onDone()))}>
            Take me off standby
          </button>
        </div>
      ) : (
        <div className="row gap-sm wrap" style={{ alignItems: 'end' }}>
          <label>
            Until
            <input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
          </label>
          <label style={{ flex: 1, minWidth: 200 }}>
            Note
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 15 minutes out" />
          </label>
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/resources/standby', { until: new Date(until).toISOString(), note: note || null }), onDone()))}>
            Put me on standby
          </button>
        </div>
      )}
      <ErrorBox error={a.error} />
    </Card>
  );
}

function Manage({ d, onDone }: { d: any; onDone: () => void }) {
  const [q, setQ] = useState('');
  const a = useAction();
  const toggle = (p: any, code: string) => {
    const has = p.resources.some((r: any) => r.code === code);
    const items = has ? p.resources.filter((r: any) => r.code !== code) : [...p.resources, { code }];
    a.run(async () => (await api.post(`/api/resources/people/${p.id}`, { items: items.map((r: any) => ({ code: r.code, expiresOn: r.expiresOn ?? null, note: r.note ?? null })) }), onDone()));
  };
  const people = d.people.filter((p: any) => !q || `${p.name} ${p.callNumber ?? ''} ${p.unit ?? ''}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card title="Who has what" actions={<input type="search" placeholder="Find someone…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find someone" style={{ maxWidth: 220 }} />}>
      <ErrorBox error={a.error} />
      <div className="table-wrap">
        <table className="table res-grid">
          <thead>
            <tr>
              <th>Name</th>
              {d.types.map((t: any) => (
                <th key={t.code}>{t.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map((p: any) => (
              <tr key={p.id}>
                <td>
                  <strong>{p.name}</strong>
                  <div className="small muted">{[p.callNumber, p.unit].filter(Boolean).join(' · ')}</div>
                </td>
                {d.types.map((t: any) => {
                  const r = p.resources.find((x: any) => x.code === t.code);
                  return (
                    <td key={t.code}>
                      <input type="checkbox" aria-label={`${p.name}: ${t.label}`} checked={!!r} disabled={a.busy} onChange={() => toggle(p, t.code)} />
                      {r?.expired && <div className="small bad-text">expired</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Settings({ d, onDone }: { d: any; onDone: () => void }) {
  const [types, setTypes] = useState<{ code?: string; label: string }[]>(d.types);
  const [anyone, setAnyone] = useState(d.settings.resourcesAnyone);
  const [cmd, setCmd] = useState(d.settings.commandAnyone);
  const a = useAction();
  return (
    <Card title="Settings">
      <label className="check">
        <input type="checkbox" checked={anyone} onChange={(e) => setAnyone(e.target.checked)} /> Every deputy can see resources on duty (otherwise supervisors and anyone given “Resources on duty”)
      </label>
      <label className="check">
        <input type="checkbox" checked={cmd} onChange={(e) => setCmd(e.target.checked)} /> Every deputy can start a command board (otherwise supervisors and anyone given “Command boards”)
      </label>
      <div className="stack sm">
        <strong className="small">Resources your agency tracks</strong>
        {types.map((t, i) => (
          <div key={i} className="row gap-sm">
            <input value={t.label} aria-label={`Resource ${i + 1}`} onChange={(e) => setTypes(types.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <button type="button" className="btn sm ghost" aria-label={`Remove ${t.label}`} onClick={() => setTypes(types.filter((_, j) => j !== i))}>
              ✕
            </button>
          </div>
        ))}
        <div>
          <button type="button" className="btn sm" onClick={() => setTypes([...types, { label: '' }])}>
            + Add a resource
          </button>
        </div>
      </div>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/resources/settings', { types: types.filter((t) => t.label.trim()), resourcesAnyone: anyone, commandAnyone: cmd }), onDone()))}>
          Save settings
        </button>
      </div>
    </Card>
  );
}

export function ResourcesPage() {
  const r = useLoad(() => api.get<any>('/api/resources'));
  const [view, setView] = useState<'now' | 'manage' | 'settings'>('now');
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const d = r.data;
  const label = (code: string) => d.types.find((t: any) => t.code === code)?.label ?? code;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Patrol"
        title="Resources on duty"
        sub="Who’s working right now — or on standby — and what they bring: breath alcohol, SFST, drone, less lethal, EMT, entry shield…"
        actions={
          (d.canManage || d.canSettings) && (
            <div className="seg-ctl" role="tablist" aria-label="View">
              <button type="button" role="tab" aria-selected={view === 'now'} className={view === 'now' ? 'on' : ''} onClick={() => setView('now')}>
                On duty now
              </button>
              {d.canManage && (
                <button type="button" role="tab" aria-selected={view === 'manage'} className={view === 'manage' ? 'on' : ''} onClick={() => setView('manage')}>
                  Who has what
                </button>
              )}
              {d.canSettings && (
                <button type="button" role="tab" aria-selected={view === 'settings'} className={view === 'settings' ? 'on' : ''} onClick={() => setView('settings')}>
                  Settings
                </button>
              )}
            </div>
          )
        }
      />
      {view === 'manage' && <Manage d={d} onDone={r.reload} />}
      {view === 'settings' && <Settings d={d} onDone={r.reload} />}
      {view === 'now' && (
        <>
          <div className="res-cards">
            {d.byType.map((t: any) => (
              <section key={t.code} className={`card res-card${t.onDuty.length ? '' : ' none'}`}>
                <header>
                  <h3>{t.label}</h3>
                  <span className={`res-n${t.onDuty.length ? '' : ' zero'}`}>{t.onDuty.length}</span>
                </header>
                {t.onDuty.length ? (
                  <ul className="plain">
                    {t.onDuty.map((p: any) => (
                      <li key={p.id}>
                        <strong>{p.name}</strong>
                        <span className="small muted">{[p.callNumber, p.car ? `car ${p.car}` : null].filter(Boolean).join(' · ')}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="small muted">No one on duty</p>
                )}
                {t.standby.length > 0 && (
                  <p className="small">
                    <span className="muted">Standby: </span>
                    {t.standby.map((p: any) => p.name).join(', ')}
                  </p>
                )}
              </section>
            ))}
          </div>
          <Card title={`On duty now (${d.onDuty.length})`}>
            {!d.onDuty.length ? (
              <p className="muted">No one is on duty by the schedule or signed on.</p>
            ) : (
              <ul className="plain res-people">
                {d.onDuty.map((p: any) => (
                  <li key={p.id}>
                    <strong>{p.name}</strong>
                    <span className="small muted">{[p.callNumber, p.shift, p.car ? `car ${p.car}` : null].filter(Boolean).join(' · ')}</span>
                    <span className="res-tags">
                      {p.resources.map((c: string) => (
                        <Badge key={c} tone="info">
                          {label(c)}
                        </Badge>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title={`On standby (${d.standby.length})`}>
            {!d.standby.length ? (
              <p className="muted">No one is on standby.</p>
            ) : (
              <ul className="plain res-people">
                {d.standby.map((p: any) => (
                  <li key={p.id}>
                    <strong>{p.name}</strong>
                    <span className="small muted">
                      {[p.callNumber, `until ${when(p.until)}`, p.note].filter(Boolean).join(' · ')}
                      {p.phone && (
                        <>
                          {' · '}
                          <a href={`tel:${p.phone}`}>{p.phone}</a>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <MyStandby d={d} onDone={r.reload} />
        </>
      )}
    </div>
  );
}
